/**
 * HR Context - Global state management for HR module
 * Provides: Candidates, Training, Evaluations, Leaves, Employee Contracts
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import * as hrService from '../../shared/utils/hrService';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import i18n from '../../i18n';
import {
  dispatchHrContractsRefresh,
  dispatchHrEvaluationsRefresh,
  dispatchHrLeavesRefresh,
  dispatchHrTrainingRefresh,
} from '../../pages/ressources-humaines/hrRefreshEvents';

const hr = (key, opts) => i18n.t(`serviceMessages.hr.${key}`, opts);

const HRContext = createContext();

export const HRProvider = ({ children }) => {
  // State for all HR entities
  const [candidates, setCandidates] = useState([]);
  const [trainingSessions, setTrainingSessions] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [employeeContracts, setEmployeeContracts] = useState([]);
  
  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // =====================================================
  // FETCH ALL DATA
  // =====================================================

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [
        candidatesData,
        trainingsData,
        evaluationsData,
        leavesData,
        contractsData
      ] = await Promise.all([
        hrService.getCandidates(),
        hrService.getTrainingSessions(),
        hrService.getEvaluations(),
        hrService.getLeaves(),
        hrService.getEmployeeContracts()
      ]);

      // Transform API data to frontend format (API returns camelCase; transforms accept snake or camel)
      setCandidates(
        sortRecordsNewestFirst(
          (candidatesData || []).map(hrService.transformCandidate).filter(Boolean),
        ),
      );
      setTrainingSessions(
        sortRecordsNewestFirst(
          (trainingsData || []).map(hrService.transformTrainingSession).filter(Boolean),
        ),
      );
      setEvaluations(
        sortRecordsNewestFirst(
          (evaluationsData || []).map(hrService.transformEvaluation).filter(Boolean),
        ),
      );
      setLeaves(
        sortRecordsNewestFirst(
          (leavesData || []).map(hrService.transformLeave).filter(Boolean),
        ),
      );
      setEmployeeContracts(
        sortRecordsNewestFirst(
          (contractsData || []).map(hrService.transformEmployeeContract).filter(Boolean),
        ),
      );
      
    } catch (err) {
      console.error('Failed to fetch HR data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // =====================================================
  // CANDIDATES CRUD
  // =====================================================

  const addCandidate = async (candidateData) => {
    try {
      const newCandidate = await hrService.createCandidate(candidateData);
      const transformed = hrService.transformCandidate(newCandidate);
      setCandidates((prev) => sortRecordsNewestFirst([...prev, transformed]));
      return transformed;
    } catch (err) {
      console.error('Failed to add candidate:', err);
      throw err;
    }
  };

  const updateCandidate = async (id, candidateData) => {
    try {
      const updated = await hrService.updateCandidate(id, candidateData);
      const transformed = hrService.transformCandidate(updated);
      setCandidates((prev) =>
        sortRecordsNewestFirst(prev.map((c) => (c.id === id ? transformed : c))),
      );
      return transformed;
    } catch (err) {
      console.error('Failed to update candidate:', err);
      throw err;
    }
  };

  const removeCandidate = async (id) => {
    try {
      await hrService.deleteCandidate(id);
      setCandidates(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      console.error('Failed to delete candidate:', err);
      throw err;
    }
  };

  // =====================================================
  // TRAINING SESSIONS CRUD
  // =====================================================

  const addTrainingSession = async (trainingData) => {
    try {
      const newTraining = await hrService.createTrainingSession(trainingData);
      const transformed = hrService.transformTrainingSession(newTraining);
      setTrainingSessions((prev) => sortRecordsNewestFirst([...prev, transformed]));
      dispatchHrTrainingRefresh();
      return transformed;
    } catch (err) {
      console.error('Failed to add training session:', err);
      throw err;
    }
  };

  const updateTrainingSession = async (id, trainingData) => {
    try {
      const updated = await hrService.updateTrainingSession(id, trainingData);
      const transformed = hrService.transformTrainingSession(updated);
      setTrainingSessions((prev) =>
        sortRecordsNewestFirst(prev.map((t) => (t.id === id ? transformed : t))),
      );
      dispatchHrTrainingRefresh();
      return transformed;
    } catch (err) {
      console.error('Failed to update training session:', err);
      throw err;
    }
  };

  const removeTrainingSession = async (id) => {
    try {
      await hrService.deleteTrainingSession(id);
      setTrainingSessions(prev => prev.filter(t => t.id !== id));
      dispatchHrTrainingRefresh();
    } catch (err) {
      console.error('Failed to delete training session:', err);
      throw err;
    }
  };

  // =====================================================
  // EVALUATIONS CRUD
  // =====================================================

  const addEvaluation = async (evaluationData) => {
    try {
      const newEvaluation = await hrService.createEvaluation(evaluationData);
      const transformed = hrService.transformEvaluation(newEvaluation);
      setEvaluations((prev) => sortRecordsNewestFirst([...prev, transformed]));
      dispatchHrEvaluationsRefresh();
      return transformed;
    } catch (err) {
      console.error('Failed to add evaluation:', err);
      throw err;
    }
  };

  const updateEvaluation = async (id, evaluationData) => {
    try {
      const updated = await hrService.updateEvaluation(id, evaluationData);
      const transformed = hrService.transformEvaluation(updated);
      setEvaluations((prev) =>
        sortRecordsNewestFirst(prev.map((e) => (e.id === id ? transformed : e))),
      );
      dispatchHrEvaluationsRefresh();
      return transformed;
    } catch (err) {
      console.error('Failed to update evaluation:', err);
      throw err;
    }
  };

  const removeEvaluation = async (id) => {
    try {
      await hrService.deleteEvaluation(id);
      setEvaluations(prev => prev.filter(e => e.id !== id));
      dispatchHrEvaluationsRefresh();
    } catch (err) {
      console.error('Failed to delete evaluation:', err);
      throw err;
    }
  };

  // =====================================================
  // LEAVES CRUD
  // =====================================================

  const addLeave = async (leaveData) => {
    try {
      const newLeave = await hrService.createLeave(leaveData);
      const transformed = hrService.transformLeave(newLeave);
      setLeaves((prev) => sortRecordsNewestFirst([...prev, transformed]));
      dispatchHrLeavesRefresh();
      return transformed;
    } catch (err) {
      console.error('Failed to add leave:', err);
      throw err;
    }
  };

  const updateLeave = async (id, leaveData) => {
    try {
      const updated = await hrService.updateLeave(id, leaveData);
      const transformed = hrService.transformLeave(updated);
      setLeaves((prev) =>
        sortRecordsNewestFirst(prev.map((l) => (l.id === id ? transformed : l))),
      );
      dispatchHrLeavesRefresh();
      return transformed;
    } catch (err) {
      console.error('Failed to update leave:', err);
      throw err;
    }
  };

  const removeLeave = async (id) => {
    try {
      await hrService.deleteLeave(id);
      setLeaves(prev => prev.filter(l => l.id !== id));
      dispatchHrLeavesRefresh();
    } catch (err) {
      console.error('Failed to delete leave:', err);
      throw err;
    }
  };

  // =====================================================
  // EMPLOYEE CONTRACTS CRUD
  // =====================================================

  const addEmployeeContract = async (contractData) => {
    try {
      const newContract = await hrService.createEmployeeContract(contractData);
      const transformed = hrService.transformEmployeeContract(newContract);
      setEmployeeContracts((prev) => sortRecordsNewestFirst([...prev, transformed]));
      dispatchHrContractsRefresh();
      return transformed;
    } catch (err) {
      console.error('Failed to add employee contract:', err);
      throw err;
    }
  };

  const updateEmployeeContract = async (id, contractData) => {
    try {
      const updated = await hrService.updateEmployeeContract(id, contractData);
      const transformed = hrService.transformEmployeeContract(updated);
      setEmployeeContracts((prev) =>
        sortRecordsNewestFirst(prev.map((c) => (c.id === id ? transformed : c))),
      );
      dispatchHrContractsRefresh();
      return transformed;
    } catch (err) {
      console.error('Failed to update employee contract:', err);
      throw err;
    }
  };

  const removeEmployeeContract = async (id) => {
    try {
      await hrService.deleteEmployeeContract(id);
      setEmployeeContracts(prev => prev.filter(c => c.id !== id));
      dispatchHrContractsRefresh();
    } catch (err) {
      console.error('Failed to delete employee contract:', err);
      throw err;
    }
  };

  // =====================================================
  // STATISTICS
  // =====================================================

  const getHRStats = useCallback(() => {
    return {
      // Candidates stats
      totalCandidates: candidates.length,
      newCandidates: candidates.filter(c => c.status === 'nouveau').length,
      interviewCandidates: candidates.filter(c => c.status === 'entretien').length,
      acceptedCandidates: candidates.filter(c => c.status === 'accepte').length,
      rejectedCandidates: candidates.filter(c => c.status === 'refuse').length,
      
      // Training stats
      totalTrainings: trainingSessions.length,
      plannedTrainings: trainingSessions.filter(t => t.status === 'planifie').length,
      ongoingTrainings: trainingSessions.filter(t => t.status === 'en_cours').length,
      completedTrainings: trainingSessions.filter(t => t.status === 'termine').length,
      totalTrainingCost: trainingSessions.reduce((sum, t) => sum + (parseFloat(t.cost) || 0), 0),
      
      // Evaluations stats
      totalEvaluations: evaluations.length,
      excellentEvaluations: evaluations.filter(e => e.noteGlobale >= 4.5).length,
      goodEvaluations: evaluations.filter(e => e.noteGlobale >= 3.5 && e.noteGlobale < 4.5).length,
      averageScore: evaluations.length > 0 
        ? (evaluations.reduce((sum, e) => sum + e.noteGlobale, 0) / evaluations.length).toFixed(2) 
        : 0,
      
      // Leaves stats
      totalLeaves: leaves.length,
      pendingLeaves: leaves.filter(l => l.status === 'en_attente').length,
      approvedLeaves: leaves.filter(l => l.status === 'approuve').length,
      rejectedLeaves: leaves.filter(l => l.status === 'refuse').length,
      totalLeaveDays: leaves.reduce((sum, l) => sum + (l.days || 0), 0),
      
      // Contracts stats
      totalContracts: employeeContracts.length,
      activeContracts: employeeContracts.filter(c => c.status === 'actif').length,
      expiringSoonContracts: employeeContracts.filter(c => c.status === 'expire_bientot').length,
      expiredContracts: employeeContracts.filter(c => c.status === 'expire').length,
      terminatedContracts: employeeContracts.filter(c => c.status === 'termine').length
    };
  }, [candidates, trainingSessions, evaluations, leaves, employeeContracts]);

  // =====================================================
  // CONTEXT VALUE
  // =====================================================

  const value = {
    // Data
    candidates,
    trainingSessions,
    evaluations,
    leaves,
    employeeContracts,
    
    // State
    loading,
    error,
    
    // Refresh
    refreshData: fetchAllData,
    
    // Candidates CRUD
    addCandidate,
    updateCandidate,
    removeCandidate,
    
    // Training CRUD
    addTrainingSession,
    updateTrainingSession,
    removeTrainingSession,
    
    // Evaluations CRUD
    addEvaluation,
    updateEvaluation,
    removeEvaluation,
    
    // Leaves CRUD
    addLeave,
    updateLeave,
    removeLeave,
    
    // Employee Contracts CRUD
    addEmployeeContract,
    updateEmployeeContract,
    removeEmployeeContract,
    
    // Stats
    getHRStats,
  };

  return (
    <HRContext.Provider value={value}>
      {children}
    </HRContext.Provider>
  );
};

export const useHR = () => {
  const context = useContext(HRContext);
  if (!context) {
    throw new Error(hr('hook_outside_provider'));
  }
  return context;
};

export default HRContext;



